import React, { useState } from 'react';
import Header from '../components/Header';
import Newsletter from '../components/Newsletter';
import Footer from '../components/Footer';

interface ReservationData {
  startDate: string;
  endDate: string;
  bike: string;
  bikePrice: number;
  options: string[];
  optionsPrice: number;
  name: string;
  email: string;
  phone: string;
  message: string;
  taille: string;
}

interface Product {
  id: string;
  name: string;
  description: string;
  pricePerDay: number;
  image: string;
  category: 'bike' | 'option';
  popular?: boolean;
}

const ReservationLocation: React.FC = () => {
  const [currentStep, setCurrentStep] = useState<number>(1);
  const [selectedStartDate, setSelectedStartDate] = useState<string>('');
  const [selectedEndDate, setSelectedEndDate] = useState<string>('');
  const [currentMonth, setCurrentMonth] = useState<Date>(new Date(2026, 0, 1));
  const [selectingEndDate, setSelectingEndDate] = useState<boolean>(false);
  const [selectedBike, setSelectedBike] = useState<string>('');
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
  const [reservationData, setReservationData] = useState<ReservationData>({
    startDate: '',
    endDate: '',
    bike: '',
    bikePrice: 0,
    options: [],
    optionsPrice: 0,
    name: '',
    email: '',
    phone: '',
    message: '',
    taille: ''
  });

  const steps = [
    { number: 1, title: "Dates", icon: "📅" },
    { number: 2, title: "Vélo", icon: "🚴" },
    { number: 3, title: "Options", icon: "⚙️" },
    { number: 4, title: "Coordonnées", icon: "👤" },
    { number: 5, title: "Confirmation", icon: "✓" }
  ];

  const bikes: Product[] = [
    {
      id: 'classique',
      name: 'Vélo Route Classique',
      description: 'Cadre carbone, groupe Shimano 105, roues aluminium',
      pricePerDay: 60,
      image: '/Photo Location Podium/Vélo Location Classique.png',
      category: 'bike'
    },
    {
      id: 'premium',
      name: 'Vélo Route Premium',
      description: 'Cadre carbone aéro, groupe Ultegra Di2, roues carbone',
      pricePerDay: 90,
      image: '/Photo Location Podium/Vélo Location Premium.png',
      category: 'bike',
      popular: true
    }
  ];

  const options: Product[] = [
    {
      id: 'casque',
      name: 'Casque',
      description: 'Casque aéro route, plusieurs tailles',
      pricePerDay: 5,
      image: '/Photo Location Podium/Casque.png',
      category: 'option'
    },
    {
      id: 'chaussures-route',
      name: 'Chaussures Route',
      description: 'Chaussures carbone, du 39 au 46',
      pricePerDay: 10,
      image: '/Photo Location Podium/Chaussures Route.png',
      category: 'option'
    },
    {
      id: 'chaussures-vtt',
      name: 'Chaussures VTT',
      description: 'Chaussures SPD, du 39 au 46',
      pricePerDay: 10,
      image: '/Photo Location Podium/Chaussures VTT.png',
      category: 'option'
    },
    {
      id: 'pedales-route',
      name: 'Pédales Auto Route',
      description: 'Pédales Look Kéo ou Shimano SPD-SL',
      pricePerDay: 8,
      image: '/Photo Location Podium/Pédales Automatique Route.png',
      category: 'option'
    },
    {
      id: 'pedales-vtt',
      name: 'Pédales Auto VTT',
      description: 'Pédales Shimano SPD',
      pricePerDay: 8,
      image: '/Photo Location Podium/Pédales Automatique VTT.png',
      category: 'option'
    },
    {
      id: 'pedales-plates',
      name: 'Pédales Plates',
      description: 'Pédales plates aluminium',
      pricePerDay: 5,
      image: '/Photo Location Podium/Pédales Plates.png',
      category: 'option'
    },
    {
      id: 'compteur',
      name: 'Compteur GPS',
      description: 'Garmin Edge avec navigation',
      pricePerDay: 15,
      image: '/Photo Location Podium/Compteur GPS.png',
      category: 'option',
      popular: true
    },
    {
      id: 'capteur',
      name: 'Capteur de Puissance',
      description: 'Capteur pédalier, compatible ANT+/BLE',
      pricePerDay: 25,
      image: '/Photo Location Podium/Capteur de Puissance.png',
      category: 'option'
    },
    {
      id: 'roues-carbone',
      name: 'Roues Carbone',
      description: 'Paire de roues carbone 50mm',
      pricePerDay: 35,
      image: '/Photo Location Podium/Roues Carbone.png',
      category: 'option'
    },
    {
      id: 'housse',
      name: 'Housse de Transport',
      description: 'Housse rembourrée pour voyage',
      pricePerDay: 20,
      image: '/Photo Location Podium/Housse de Transport.png',
      category: 'option'
    },
    {
      id: 'sacoche-selle',
      name: 'Sacoche de Selle',
      description: 'Sacoche bikepacking étanche',
      pricePerDay: 8,
      image: '/Photo Location Podium/Sacoche de Selle.png',
      category: 'option'
    },
    {
      id: 'sacoche-cadre',
      name: 'Sacoche de Cadre',
      description: 'Sacoche triangle étanche',
      pricePerDay: 6,
      image: '/Photo Location Podium/Sacoche de Cadre.png',
      category: 'option'
    },
    {
      id: 'sacoche-guidon',
      name: 'Sacoche de Guidon',
      description: 'Sacoche avant étanche',
      pricePerDay: 8,
      image: '/Photo Location Podium/Sacoche de Guidon.png',
      category: 'option'
    },
    {
      id: 'combinaison',
      name: 'Combinaison Néoprène',
      description: 'Pour triathlon, plusieurs tailles',
      pricePerDay: 25,
      image: '/Photo Location Podium/Combinaison Néoprène.png',
      category: 'option'
    }
  ];

  const tailles = ['XS', 'S', 'M', 'L', 'XL'];

  // Calendar helpers
  const getDaysInMonth = (date: Date): number => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date: Date): number => {
    const day = new Date(date.getFullYear(), date.getMonth(), 1).getDay();
    return day === 0 ? 6 : day - 1;
  };

  const formatMonth = (date: Date): string => {
    return date.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
  };

  const formatSelectedDate = (dateStr: string): string => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', { 
      weekday: 'long', 
      day: 'numeric', 
      month: 'long', 
      year: 'numeric' 
    });
  };

  const calculateDays = (): number => {
    if (!selectedStartDate || !selectedEndDate) return 0;
    const start = new Date(selectedStartDate);
    const end = new Date(selectedEndDate);
    const diff = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;
    return diff > 0 ? diff : 0;
  };

  const isPastDay = (day: number): boolean => {
    const today = new Date();
    const checkDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    today.setHours(0, 0, 0, 0);
    return checkDate < today;
  };

  const isInRange = (day: number): boolean => {
    if (!selectedStartDate || !selectedEndDate) return false;
    const checkDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    const start = new Date(selectedStartDate);
    const end = new Date(selectedEndDate);
    return checkDate > start && checkDate < end;
  };

  const isStartDate = (day: number): boolean => {
    const checkDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day).toISOString().split('T')[0];
    return selectedStartDate === checkDate;
  };

  const isEndDate = (day: number): boolean => {
    const checkDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day).toISOString().split('T')[0];
    return selectedEndDate === checkDate;
  };

  const handleDateSelect = (day: number) => {
    const selected = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    const dateStr = selected.toISOString().split('T')[0];
    
    if (!selectingEndDate || !selectedStartDate) {
      setSelectedStartDate(dateStr);
      setSelectedEndDate('');
      setSelectingEndDate(true);
      setReservationData({ ...reservationData, startDate: dateStr, endDate: '' });
    } else {
      if (new Date(dateStr) < new Date(selectedStartDate)) {
        setSelectedStartDate(dateStr);
        setSelectedEndDate('');
        setReservationData({ ...reservationData, startDate: dateStr, endDate: '' });
      } else {
        setSelectedEndDate(dateStr);
        setSelectingEndDate(false);
        setReservationData({ ...reservationData, endDate: dateStr });
      }
    }
  };

  const handlePrevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const handleBikeSelect = (bike: Product) => {
    setSelectedBike(bike.id);
    setReservationData({ 
      ...reservationData, 
      bike: bike.name, 
      bikePrice: bike.pricePerDay 
    });
  };

  const handleOptionToggle = (option: Product) => {
    const isSelected = selectedOptions.includes(option.id);
    let newOptions: string[];
    let newOptionsPrice: number;

    if (isSelected) {
      newOptions = selectedOptions.filter(id => id !== option.id);
    } else {
      newOptions = [...selectedOptions, option.id];
    }

    newOptionsPrice = newOptions.reduce((total, optId) => {
      const opt = options.find(o => o.id === optId);
      return total + (opt?.pricePerDay || 0);
    }, 0);

    setSelectedOptions(newOptions);
    setReservationData({ 
      ...reservationData, 
      options: newOptions.map(id => options.find(o => o.id === id)?.name || ''),
      optionsPrice: newOptionsPrice
    });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setReservationData({ ...reservationData, [name]: value });
  };

  const handleNext = () => {
    if (currentStep < 5) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handlePayment = () => {
    alert('Redirection vers le paiement sécurisé...');
  };

  const getTotalPrice = (): number => {
    const days = calculateDays();
    return (reservationData.bikePrice + reservationData.optionsPrice) * days;
  };

  const canProceed = (): boolean => {
    switch (currentStep) {
      case 1:
        return !!selectedStartDate && !!selectedEndDate;
      case 2:
        return !!selectedBike && !!reservationData.taille;
      case 3:
        return true; // Options are optional
      case 4:
        return !!reservationData.name && !!reservationData.email && !!reservationData.phone;
      default:
        return true;
    }
  };

  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth(currentMonth);
    const firstDay = getFirstDayOfMonth(currentMonth);
    const days = [];
    const weekDays = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="h-12"></div>);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const isPast = isPastDay(day);
      const inRange = isInRange(day);
      const isStart = isStartDate(day);
      const isEnd = isEndDate(day);
      const isDisabled = isPast;

      days.push(
        <button
          key={day}
          onClick={() => !isDisabled && handleDateSelect(day)}
          disabled={isDisabled}
          className={`h-12 w-12 flex items-center justify-center text-sm font-medium transition-all duration-200 relative
            ${isStart || isEnd
              ? 'bg-gradient-to-br from-zinc-900 to-zinc-700 text-white rounded-xl shadow-lg z-10' 
              : inRange
                ? 'bg-zinc-200 text-zinc-900'
                : ''}
            ${isStart ? 'rounded-r-none' : ''}
            ${isEnd ? 'rounded-l-none' : ''}
            ${isDisabled 
              ? 'text-zinc-300 cursor-not-allowed' 
              : !isStart && !isEnd && !inRange ? 'hover:bg-zinc-100 text-zinc-700 cursor-pointer rounded-xl' : ''}
          `}
        >
          {day}
        </button>
      );
    }

    return (
      <div className="bg-white rounded-2xl shadow-xl border border-zinc-100 p-6 w-full max-w-md">
        <div className="flex items-center justify-between mb-6">
          <button 
            onClick={handlePrevMonth} 
            className="p-3 hover:bg-zinc-100 rounded-xl transition-all duration-300"
          >
            <svg className="w-5 h-5 text-zinc-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h3 className="text-xl font-bold capitalize text-zinc-900">{formatMonth(currentMonth)}</h3>
          <button 
            onClick={handleNextMonth} 
            className="p-3 hover:bg-zinc-100 rounded-xl transition-all duration-300"
          >
            <svg className="w-5 h-5 text-zinc-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>

        <div className="grid grid-cols-7 gap-1 mb-3">
          {weekDays.map(day => (
            <div key={day} className="h-10 flex items-center justify-center text-xs font-semibold text-zinc-400 uppercase tracking-wider">
              {day}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-1">
          {days}
        </div>

        {/* Date Selection Info */}
        <div className="mt-6 p-4 bg-zinc-50 rounded-xl">
          {!selectedStartDate ? (
            <p className="text-zinc-500 text-sm text-center">Sélectionnez la date de début</p>
          ) : !selectedEndDate ? (
            <p className="text-zinc-500 text-sm text-center">Sélectionnez la date de fin</p>
          ) : (
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-zinc-500">Du</span>
                <span className="font-medium text-zinc-900">{formatSelectedDate(selectedStartDate)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-zinc-500">Au</span>
                <span className="font-medium text-zinc-900">{formatSelectedDate(selectedEndDate)}</span>
              </div>
              <div className="flex justify-between text-sm pt-2 border-t border-zinc-200">
                <span className="font-semibold text-zinc-900">Durée</span>
                <span className="font-bold text-zinc-900">{calculateDays()} jour{calculateDays() > 1 ? 's' : ''}</span>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="flex justify-center">
            {renderCalendar()}
          </div>
        );

      case 2:
        return (
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              {bikes.map((bike) => (
                <div
                  key={bike.id}
                  onClick={() => handleBikeSelect(bike)}
                  className={`relative bg-white rounded-2xl overflow-hidden cursor-pointer transition-all duration-300 border-2
                    ${selectedBike === bike.id 
                      ? 'border-zinc-900 shadow-2xl scale-[1.02]' 
                      : 'border-zinc-200 hover:border-zinc-400 hover:shadow-xl'}
                    ${bike.popular ? 'ring-2 ring-amber-400 ring-offset-2' : ''}
                  `}
                >
                  {bike.popular && (
                    <div className="absolute top-4 right-4 bg-gradient-to-r from-amber-400 to-amber-500 text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg z-10">
                      POPULAIRE
                    </div>
                  )}
                  
                  <div className="aspect-video bg-zinc-50 p-4">
                    <img 
                      src={bike.image} 
                      alt={bike.name}
                      className="w-full h-full object-contain"
                    />
                  </div>
                  
                  <div className="p-6">
                    <div className="flex justify-between items-start mb-2">
                      <h3 className="text-xl font-bold text-zinc-900">{bike.name}</h3>
                      <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center
                        ${selectedBike === bike.id ? 'border-zinc-900 bg-zinc-900' : 'border-zinc-300'}
                      `}>
                        {selectedBike === bike.id && (
                          <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </div>
                    </div>
                    <p className="text-zinc-500 text-sm mb-4">{bike.description}</p>
                    <p className="text-2xl font-black text-zinc-900">{bike.pricePerDay}€<span className="text-sm font-normal text-zinc-500">/jour</span></p>
                  </div>
                </div>
              ))}
            </div>

            {/* Taille selection */}
            <div className="bg-white rounded-2xl shadow-xl border border-zinc-100 p-6 max-w-md mx-auto">
              <h4 className="text-lg font-bold text-zinc-900 mb-4">Votre taille de cadre</h4>
              <div className="flex flex-wrap gap-3">
                {tailles.map((taille) => (
                  <button
                    key={taille}
                    onClick={() => setReservationData({ ...reservationData, taille })}
                    className={`px-6 py-3 rounded-xl font-medium transition-all
                      ${reservationData.taille === taille
                        ? 'bg-zinc-900 text-white'
                        : 'bg-zinc-100 text-zinc-700 hover:bg-zinc-200'
                      }
                    `}
                  >
                    {taille}
                  </button>
                ))}
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="max-w-5xl mx-auto">
            <p className="text-center text-zinc-500 mb-8">Sélectionnez les options que vous souhaitez ajouter à votre location</p>
            
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {options.map((option) => (
                <div
                  key={option.id}
                  onClick={() => handleOptionToggle(option)}
                  className={`relative bg-white rounded-xl p-4 cursor-pointer transition-all duration-300 border-2
                    ${selectedOptions.includes(option.id) 
                      ? 'border-zinc-900 shadow-lg' 
                      : 'border-zinc-200 hover:border-zinc-400'}
                  `}
                >
                  {option.popular && (
                    <div className="absolute -top-2 -right-2 bg-amber-400 text-white text-xs font-bold w-6 h-6 rounded-full flex items-center justify-center">
                      ★
                    </div>
                  )}
                  
                  <div className="aspect-square bg-zinc-50 rounded-lg mb-3 p-2">
                    <img 
                      src={option.image} 
                      alt={option.name}
                      className="w-full h-full object-contain"
                    />
                  </div>
                  
                  <h4 className="font-semibold text-zinc-900 text-sm mb-1">{option.name}</h4>
                  <p className="text-xs text-zinc-500 mb-2">{option.description}</p>
                  <div className="flex justify-between items-center">
                    <p className="font-bold text-zinc-900">{option.pricePerDay}€<span className="text-xs font-normal text-zinc-500">/j</span></p>
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center
                      ${selectedOptions.includes(option.id) ? 'border-zinc-900 bg-zinc-900' : 'border-zinc-300'}
                    `}>
                      {selectedOptions.includes(option.id) && (
                        <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Selected options summary */}
            {selectedOptions.length > 0 && (
              <div className="mt-8 bg-zinc-50 rounded-xl p-4">
                <h4 className="font-semibold text-zinc-900 mb-2">Options sélectionnées ({selectedOptions.length})</h4>
                <div className="flex flex-wrap gap-2">
                  {selectedOptions.map(optId => {
                    const opt = options.find(o => o.id === optId);
                    return opt && (
                      <span key={optId} className="bg-zinc-900 text-white text-sm px-3 py-1 rounded-full">
                        {opt.name} (+{opt.pricePerDay}€/j)
                      </span>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        );

      case 4:
        return (
          <div className="max-w-lg mx-auto">
            <div className="bg-white rounded-2xl shadow-xl border border-zinc-100 p-8">
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-zinc-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-zinc-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-zinc-900">Vos coordonnées</h3>
                <p className="text-zinc-500 text-sm mt-1">Pour finaliser votre réservation</p>
              </div>

              <div className="space-y-5">
                <div>
                  <label className="block text-sm font-semibold text-zinc-700 mb-2">
                    Nom complet <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={reservationData.name}
                    onChange={handleInputChange}
                    placeholder="Jean Dupont"
                    className="w-full px-4 py-3.5 bg-zinc-50 border border-zinc-200 rounded-xl focus:ring-2 focus:ring-zinc-900 focus:border-transparent transition-all text-zinc-900 placeholder-zinc-400"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-zinc-700 mb-2">
                    Email <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={reservationData.email}
                    onChange={handleInputChange}
                    placeholder="jean.dupont@email.com"
                    className="w-full px-4 py-3.5 bg-zinc-50 border border-zinc-200 rounded-xl focus:ring-2 focus:ring-zinc-900 focus:border-transparent transition-all text-zinc-900 placeholder-zinc-400"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-zinc-700 mb-2">
                    Téléphone <span className="text-rose-500">*</span>
                  </label>
                  <div className="flex">
                    <div className="flex items-center px-4 bg-zinc-100 border border-r-0 border-zinc-200 rounded-l-xl">
                      <span className="text-lg">🇫🇷</span>
                      <span className="ml-2 text-sm text-zinc-600 font-medium">+33</span>
                    </div>
                    <input
                      type="tel"
                      name="phone"
                      value={reservationData.phone}
                      onChange={handleInputChange}
                      placeholder="6 12 34 56 78"
                      className="flex-1 px-4 py-3.5 bg-zinc-50 border border-zinc-200 rounded-r-xl focus:ring-2 focus:ring-zinc-900 focus:border-transparent transition-all text-zinc-900 placeholder-zinc-400"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-zinc-700 mb-2">
                    Message (optionnel)
                  </label>
                  <textarea
                    name="message"
                    value={reservationData.message}
                    onChange={handleInputChange}
                    placeholder="Précisions sur votre pratique, questions..."
                    rows={3}
                    className="w-full px-4 py-3.5 bg-zinc-50 border border-zinc-200 rounded-xl focus:ring-2 focus:ring-zinc-900 focus:border-transparent transition-all text-zinc-900 placeholder-zinc-400 resize-none"
                  />
                </div>
              </div>
            </div>
          </div>
        );

      case 5:
        return (
          <div className="max-w-lg mx-auto">
            <div className="bg-white rounded-2xl shadow-xl border border-zinc-100 overflow-hidden">
              <div className="bg-gradient-to-br from-zinc-900 to-zinc-700 p-8 text-center">
                <div className="w-20 h-20 bg-white/10 backdrop-blur rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold text-white">Récapitulatif</h3>
                <p className="text-zinc-300 mt-2">Vérifiez votre réservation</p>
              </div>

              <div className="p-6 space-y-4">
                <div className="flex items-center gap-4 p-4 bg-zinc-50 rounded-xl">
                  <div className="w-12 h-12 bg-zinc-200 rounded-lg flex items-center justify-center">
                    <span className="text-2xl">📅</span>
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-zinc-500">Période de location</p>
                    <p className="font-semibold text-zinc-900 text-sm">{formatSelectedDate(reservationData.startDate)}</p>
                    <p className="font-semibold text-zinc-900 text-sm">→ {formatSelectedDate(reservationData.endDate)}</p>
                    <p className="text-zinc-600 text-sm mt-1">{calculateDays()} jour{calculateDays() > 1 ? 's' : ''}</p>
                  </div>
                </div>

                <div className="flex items-center gap-4 p-4 bg-zinc-50 rounded-xl">
                  <div className="w-12 h-12 bg-zinc-200 rounded-lg flex items-center justify-center">
                    <span className="text-2xl">🚴</span>
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-zinc-500">Vélo</p>
                    <p className="font-semibold text-zinc-900">{reservationData.bike}</p>
                    <p className="text-zinc-600 text-sm">Taille {reservationData.taille} • {reservationData.bikePrice}€/jour</p>
                  </div>
                </div>

                {selectedOptions.length > 0 && (
                  <div className="p-4 bg-zinc-50 rounded-xl">
                    <div className="flex items-center gap-4 mb-3">
                      <div className="w-12 h-12 bg-zinc-200 rounded-lg flex items-center justify-center">
                        <span className="text-2xl">⚙️</span>
                      </div>
                      <div>
                        <p className="text-sm text-zinc-500">Options ({selectedOptions.length})</p>
                        <p className="font-semibold text-zinc-900">{reservationData.optionsPrice}€/jour</p>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2 ml-16">
                      {reservationData.options.map((opt, idx) => (
                        <span key={idx} className="bg-zinc-200 text-zinc-700 text-xs px-2 py-1 rounded-full">
                          {opt}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex items-center gap-4 p-4 bg-zinc-50 rounded-xl">
                  <div className="w-12 h-12 bg-zinc-200 rounded-lg flex items-center justify-center">
                    <span className="text-2xl">👤</span>
                  </div>
                  <div>
                    <p className="text-sm text-zinc-500">Contact</p>
                    <p className="font-semibold text-zinc-900">{reservationData.name}</p>
                    <p className="text-zinc-600 text-sm">{reservationData.email}</p>
                  </div>
                </div>
              </div>

              <div className="border-t border-zinc-200 p-6">
                <div className="space-y-2 mb-4">
                  <div className="flex justify-between text-sm">
                    <span className="text-zinc-500">Vélo ({calculateDays()}j × {reservationData.bikePrice}€)</span>
                    <span className="text-zinc-900">{reservationData.bikePrice * calculateDays()}€</span>
                  </div>
                  {reservationData.optionsPrice > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-zinc-500">Options ({calculateDays()}j × {reservationData.optionsPrice}€)</span>
                      <span className="text-zinc-900">{reservationData.optionsPrice * calculateDays()}€</span>
                    </div>
                  )}
                </div>
                <div className="flex justify-between items-center pt-4 border-t border-zinc-200">
                  <span className="text-lg font-semibold text-zinc-900">Total</span>
                  <span className="text-3xl font-black text-zinc-900">{getTotalPrice()}€</span>
                </div>
                <p className="text-sm text-zinc-500 mt-2">
                  + Caution de 500€ à prévoir (restituée au retour)
                </p>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-zinc-50 to-zinc-100">
      <Header />
      
      <section className="pt-24 md:pt-32 pb-16 md:pb-24 min-h-screen">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h1 className="text-3xl md:text-5xl font-black text-zinc-900 mb-4">
              Réserver ma location
            </h1>
            <p className="text-zinc-600 text-lg max-w-2xl mx-auto">
              Choisissez votre vélo et vos options pour une expérience sur mesure
            </p>
          </div>

          {/* Progress Steps */}
          <div className="mb-12">
            <div className="flex items-center justify-center max-w-4xl mx-auto">
              {steps.map((step, index) => (
                <React.Fragment key={step.number}>
                  <div 
                    className={`flex flex-col items-center cursor-pointer transition-all duration-300
                      ${step.number <= currentStep ? 'opacity-100' : 'opacity-40'}
                    `}
                    onClick={() => step.number < currentStep && setCurrentStep(step.number)}
                  >
                    <div className={`w-12 h-12 md:w-14 md:h-14 rounded-2xl flex items-center justify-center text-xl md:text-2xl transition-all duration-300 shadow-lg
                      ${currentStep === step.number 
                        ? 'bg-gradient-to-br from-zinc-900 to-zinc-700 text-white scale-110' 
                        : currentStep > step.number
                          ? 'bg-emerald-500 text-white'
                          : 'bg-white text-zinc-400 border border-zinc-200'}
                    `}>
                      {currentStep > step.number ? '✓' : step.icon}
                    </div>
                    <span className={`mt-2 text-xs md:text-sm font-medium hidden md:block
                      ${currentStep === step.number ? 'text-zinc-900' : 'text-zinc-500'}
                    `}>
                      {step.title}
                    </span>
                  </div>
                  
                  {index < steps.length - 1 && (
                    <div className={`flex-1 h-1 mx-2 md:mx-4 rounded-full transition-all duration-500
                      ${step.number < currentStep ? 'bg-emerald-500' : 'bg-zinc-200'}
                    `} />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>

          {/* Step Content */}
          <div className="min-h-[500px]">
            {renderStepContent()}
          </div>

          {/* Navigation */}
          <div className="flex justify-between items-center max-w-4xl mx-auto mt-12">
            {currentStep > 1 ? (
              <button
                onClick={handleBack}
                className="px-6 py-3 rounded-xl font-semibold text-zinc-600 hover:text-zinc-900 hover:bg-zinc-100 transition-all duration-300 flex items-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Retour
              </button>
            ) : (
              <div></div>
            )}

            {currentStep < 5 ? (
              <button
                onClick={handleNext}
                disabled={!canProceed()}
                className={`px-8 py-4 rounded-xl font-bold text-lg transition-all duration-300 flex items-center gap-2 shadow-lg
                  ${canProceed()
                    ? 'bg-gradient-to-r from-zinc-900 to-zinc-700 text-white hover:shadow-xl hover:scale-105'
                    : 'bg-zinc-200 text-zinc-400 cursor-not-allowed'}
                `}
              >
                Continuer
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            ) : (
              <button
                onClick={handlePayment}
                className="px-8 py-4 rounded-xl font-bold text-lg bg-gradient-to-r from-emerald-600 to-emerald-500 text-white hover:shadow-xl hover:scale-105 transition-all duration-300 flex items-center gap-2 shadow-lg"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                Payer {getTotalPrice()}€
              </button>
            )}
          </div>
        </div>
      </section>

      <Newsletter />
      <Footer />
    </div>
  );
};

export default ReservationLocation;
